import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "@phosphor-icons/react";

const Services = () => {
  const [openCategory, setOpenCategory] = useState(0);

  const serviceCategories = [
    {
      title: "Facial Balancing & Contouring",
      description: "Artistically designed treatments to harmonize your natural features and create balanced proportions.",
      treatments: [
        {
          name: "Cheek Enhancement",
          description: "Restore volume and define cheekbones for a sculpted, youthful appearance.",
          duration: "30-45 min",
        },
        {
          name: "Jawline Definition",
          description: "Create a sharper, more defined jawline using advanced filler techniques.",
          duration: "45-60 min",
        },
        {
          name: "Chin Augmentation",
          description: "Balance facial proportions with non-surgical chin projection enhancement.",
          duration: "30 min",
        },
        {
          name: "Temple Restoration",
          description: "Address hollowing to restore youthful facial fullness.",
          duration: "30 min",
        },
      ],
    },
    {
      title: "Injectable Treatments",
      description: "Precision techniques for subtle, natural-looking enhancement and wrinkle reduction.",
      treatments: [
        {
          name: "Neurotoxin Treatments",
          description: "Soften fine lines and prevent wrinkles with expertly placed Botox or Dysport.",
          duration: "15-30 min",
        },
        {
          name: "Lip Enhancement",
          description: "Achieve beautifully balanced lips with subtle volume and definition.",
          duration: "30-45 min",
        },
        {
          name: "Under-Eye Rejuvenation",
          description: "Address dark circles and hollows for a refreshed, rested appearance.",
          duration: "30 min",
        },
        {
          name: "Nasolabial Fold Treatment",
          description: "Soften smile lines for a smoother, more youthful look.",
          duration: "30 min",
        },
      ],
    },
    {
      title: "Skin Rejuvenation",
      description: "Advanced protocols for luminous, youthful skin using cutting-edge technologies.",
      treatments: [
        {
          name: "Medical-Grade Facials",
          description: "Customized treatments addressing your unique skin concerns.",
          duration: "60-90 min",
        },
        {
          name: "Chemical Peels",
          description: "Reveal fresh, radiant skin with professionally administered peels.",
          duration: "45-60 min",
        },
        {
          name: "Microneedling",
          description: "Stimulate collagen production for improved texture and tone.",
          duration: "60 min",
        },
        {
          name: "Laser Treatments",
          description: "Target pigmentation, fine lines, and skin laxity with precision.",
          duration: "30-60 min",
        },
      ],
    },
    {
      title: "Body Contouring",
      description: "Non-invasive treatments to sculpt and refine your silhouette.",
      treatments: [
        {
          name: "CoolSculpting",
          description: "Freeze away stubborn fat cells for a slimmer contour.",
          duration: "35-60 min per area",
        },
        {
          name: "Body Tightening",
          description: "Firm and tone skin with radiofrequency technology.",
          duration: "45-60 min",
        },
        {
          name: "Cellulite Reduction",
          description: "Smooth and improve skin texture on thighs and buttocks.",
          duration: "45 min",
        },
      ],
    },
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Our Services
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-matte">
              Artistry in
              <br />
              <span className="italic">Aesthetics</span>
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed max-w-xl">
              Each treatment is thoughtfully customized to your unique facial anatomy 
              and aesthetic goals. We combine medical precision with artistic vision 
              for results that are naturally beautiful.
            </p>
          </div>
        </div>
      </section>

      {/* Services Accordion */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {serviceCategories.map((category, categoryIndex) => (
              <div
                key={category.title}
                data-testid={`service-category-${categoryIndex}`}
                className="border-b border-matte/10"
              >
                <button
                  onClick={() =>
                    setOpenCategory(openCategory === categoryIndex ? -1 : categoryIndex)
                  }
                  data-testid={`service-toggle-${categoryIndex}`}
                  className="w-full py-8 flex items-center justify-between text-left group"
                >
                  <div>
                    <h2 className="font-serif text-2xl md:text-3xl font-light text-matte group-hover:text-taupe transition-colors duration-300">
                      {category.title}
                    </h2>
                    <p className="text-sm font-light text-matte/50 mt-2 max-w-lg">
                      {category.description}
                    </p>
                  </div>
                  <div className="text-taupe ml-4 flex-shrink-0">
                    {openCategory === categoryIndex ? (
                      <Minus size={24} weight="light" />
                    ) : (
                      <Plus size={24} weight="light" />
                    )}
                  </div>
                </button>

                {openCategory === categoryIndex && (
                  <div
                    data-testid={`service-content-${categoryIndex}`}
                    className="pb-8 animate-fade-in"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {category.treatments.map((treatment, treatmentIndex) => (
                        <div
                          key={treatment.name}
                          data-testid={`treatment-${categoryIndex}-${treatmentIndex}`}
                          className="bg-white p-6 border border-transparent hover:border-taupe/20 transition-all duration-300"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-serif text-lg text-matte">
                              {treatment.name}
                            </h3>
                            <span className="text-xs text-taupe uppercase tracking-wider">
                              {treatment.duration}
                            </span>
                          </div>
                          <p className="text-sm font-light text-matte/60 leading-relaxed">
                            {treatment.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="image-reveal aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1727386245813-c70d8883995e?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Facial aesthetics concept"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Our Approach
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
                Customized
                <br />
                Facial Design
              </h2>
              <p className="mt-8 text-base font-light text-matte/70 leading-relaxed">
                No two faces are alike, and neither are our treatment plans. We begin 
                every journey with a comprehensive consultation, analyzing your unique 
                facial anatomy, skin quality, and personal goals.
              </p>
              <p className="mt-6 text-base font-light text-matte/70 leading-relaxed">
                Using our artistic approach to facial balancing, we create a customized 
                treatment plan that enhances your natural features while maintaining 
                the harmony and proportion that define beautiful aesthetics.
              </p>
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-4">
                  <span className="w-2 h-2 bg-taupe rounded-full" />
                  <span className="text-sm text-matte/70">Comprehensive facial analysis</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="w-2 h-2 bg-taupe rounded-full" />
                  <span className="text-sm text-matte/70">Customized treatment planning</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="w-2 h-2 bg-taupe rounded-full" />
                  <span className="text-sm text-matte/70">Natural-looking results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
            Ready to Transform?
          </h2>
          <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
            Schedule your personalized consultation and discover your customized 
            treatment plan.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="btn-primary inline-flex items-center gap-3 mt-10"
          >
            Book Consultation
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
